import {
  HHBox,
  HHButton,
  HHSnackbarAlert,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useNavigate } from 'react-router-dom';
import { LocalStorage } from '../../../../../utils/local-storage';
import { tabRoutes } from '../../constants/hinge-connect-constants.constant';
import { useSnackbar } from '../../hooks/use-snackbar';
import {
  ListRulesContextProvider,
  LOCAL_STORAGE_KEYS,
  useListRulesContext,
} from '../contexts/list-rules.context';
import { useDeleteRule } from '../hooks/use-delete-rule';
import { useRulesPermissions } from '../hooks/use-rules-permissions';
import { CreateRuleFab } from './create-rule-fab';
import { ListRulesGrid } from './list-rules-grid';
import { ListRulesToolbar } from './list-rules-toolbar';

export const ListRulesViewContainer = (): JSX.Element => {
  const navigate = useNavigate();
  const permissions = useRulesPermissions();
  const { listRulesData, listRulesLoading, refetchListRules, listRulesError } =
    useListRulesContext();
  const { selectedSource } = useListRulesContext();

  const {
    deleteRule,
    data: deleteRuleData,
    loading: deleteRuleLoading,
    error: deleteRuleError,
    errorMessages: deleteRuleErrorMessages,
  } = useDeleteRule((): void => {
    // do nothing
  });

  const {
    snackbar: deleteRuleSnackbar,
    handleClose: handleDeleteRuleSnackbarClose,
  } = useSnackbar({
    data: deleteRuleData,
    error: deleteRuleError,
    errorMessages: deleteRuleErrorMessages,
    successMessage: 'Rule deleted successfully',
  });

  return (
    <>
      <HHSnackbarAlert
        message={deleteRuleSnackbar.message}
        severity={deleteRuleSnackbar.severity}
        verticalAlignment="top"
        horizontalAlignment="center"
        hhVariant="standard"
        open={deleteRuleSnackbar.open}
        onClose={handleDeleteRuleSnackbarClose}
        autoHideTime={
          deleteRuleSnackbar.severity === 'success' ? 3000 : undefined
        }
      />
      <HHStack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
      >
        <HHStack direction="column" spacing={0}>
          <HHTypography hhVariant="h2">Rules</HHTypography>
          {selectedSource && (
            <HHTypography hhVariant="body2" sx={{ color: 'text.secondary' }}>
              Source: {selectedSource}
            </HHTypography>
          )}
        </HHStack>
        <HHButton
          hhVariant="text"
          onClick={(): void => {
            // Clear all filters to return to sources context
            LocalStorage.set(LOCAL_STORAGE_KEYS.selectedSource, '');
            LocalStorage.set(LOCAL_STORAGE_KEYS.selectedStatus, '');
            LocalStorage.set(LOCAL_STORAGE_KEYS.selectedResourceTypes, []);
            navigate(`/${tabRoutes.baseRoute}/${tabRoutes.rules}`);
          }}
        >
          Back to Sources
        </HHButton>
      </HHStack>
      <ListRulesToolbar rules={listRulesData?.listRules?.rules ?? []} />
      <HHStack direction="row" justifyContent="space-between">
        <ListRulesGrid
          listRulesData={listRulesData}
          listRulesLoading={listRulesLoading}
          refetchListRules={refetchListRules}
          deleteRule={deleteRule}
          deleteRuleLoading={deleteRuleLoading}
          listRulesError={listRulesError}
          copyRuleLoading={false}
          runRuleLoading={false}
        />
        {permissions.canCreate && <CreateRuleFab />}
      </HHStack>
    </>
  );
};

export const ListRulesView = (): JSX.Element => (
  <ListRulesContextProvider>
    <HHBox paddingY="1rem">
      <ListRulesViewContainer />
    </HHBox>
  </ListRulesContextProvider>
);
